import { useState, useEffect } from 'react'
import { Plus } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { DialogTrigger } from '@/components/ui/dialog'
import {
  useCreateOrgInviteBatch,
  getOrgUsers,
} from '@/services/user-management'
import { toast } from 'react-hot-toast'
import { useTokenAndOrgId } from '@/services/utils'
import { useActiveOrgProfile } from '@/hooks/useActiveOrgProfile'
import { TabsContent, TabsList, TabsTrigger, Tabs } from '@/components/ui/tabs'
import { UserOrganization } from '@/services/inference.gen'

import { ManualEntryTab } from './ManualEntryTab'
import { CsvUploadTab } from './CsvUploadTab'
import { EmailList } from './EmailList'
import { EmailEntry } from './types'

// Main component
export const InviteUsers = () => {
  const { token, orgId } = useTokenAndOrgId()

  const [isOpen, setIsOpen] = useState(false)
  const [emails, setEmails] = useState<EmailEntry[]>([])
  const [existingUsers, setExistingUsers] = useState<UserOrganization[]>([])

  const { data: orgData } = useActiveOrgProfile()

  const storageLimitGB =
    orgData?.org.metadata.limits.org_per_user_storage_gb || 0

  // Fetch existing users
  useEffect(() => {
    if (!isOpen || !token || !orgId) return

    const fetchOrgUsers = async () => {
      try {
        const activeUsers = await getOrgUsers({
          page_size: 100,
          page_number: 1,
          status: 'active',
          search_text: '',
          sort_by: 'created_at',
          sort_direction: 'desc',
        })
        const invitedUsers = await getOrgUsers({
          page_size: 100,
          page_number: 1,
          status: 'invited',
          search_text: '',
          sort_by: 'created_at',
          sort_direction: 'desc',
        })

        setExistingUsers([...activeUsers.users, ...invitedUsers.users])
      } catch (error) {
        console.error('Failed to fetch existing users:', error)
        toast.error(JSON.stringify(error))
      }
    }

    fetchOrgUsers()
  }, [isOpen, token, orgId])

  // Email list management
  const addEmails = (newEmails: EmailEntry[]) => {
    setEmails((prev) => [...prev, ...newEmails])
  }

  const removeEmail = (emailToRemove: string) => {
    setEmails((prev) => prev.filter((email) => email.email !== emailToRemove))
  }

  const removeAllEmails = () => {
    setEmails([])
  }

  // API integration
  const { mutateAsync: batchInvite, isPending: sendingInvites } =
    useCreateOrgInviteBatch()

  const onInviteSubmit = async () => {
    await batchInvite(
      emails.map((email) => ({ email: email.email, role: email.type })),
      {
        onSuccess: () => {
          setIsOpen(false)
          setEmails([])
        },
      }
    )
  }

  const validateEmail = (
    email: string
  ): { valid: boolean; errorMessage: string } => {
    const trimmedEmail = email.trim()

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
      return {
        valid: false,
        errorMessage: 'Please enter a valid email address.',
      }
    }

    const existingUser = existingUsers.find(
      (user) => user.user?.email === trimmedEmail
    )
    if (existingUser?.status === 'active') {
      return {
        valid: false,
        errorMessage: `${trimmedEmail} is already a member of the organization.`,
      }
    }

    if (emails.some((e) => e.email === trimmedEmail)) {
      return {
        valid: false,
        errorMessage: `${trimmedEmail} is already in the invite list.`,
      }
    }

    // Here we're just validating, but the type is preserved for API consistency
    return { valid: true, errorMessage: '' }
  }

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <Button data-testid="invite-users-button">
          <Plus className="w-4 h-4 mr-2" />
          Invite Users
        </Button>
      </DialogTrigger>

      <DialogContent
        className="sm:max-w-[525px]"
        data-testid="invite-users-dialog"
      >
        <DialogHeader>
          <DialogTitle data-testid="invite-users-dialog-title">
            Invite new users
          </DialogTitle>
        </DialogHeader>

        <Tabs
          defaultValue="manual"
          className="w-full"
          data-testid="invite-users-tabs"
        >
          <TabsList>
            <TabsTrigger value="manual" data-testid="manual-entry-tab">
              Manual Entry
            </TabsTrigger>
            <TabsTrigger value="csv" data-testid="csv-upload-tab">
              CSV Upload
            </TabsTrigger>
          </TabsList>

          <TabsContent value="manual" data-testid="manual-entry-content">
            <ManualEntryTab
              onEmailAdd={(email) => {
                const validation = validateEmail(email.email)
                if (validation.valid) {
                  addEmails([email])
                  return { success: true, message: '' }
                }
                return { success: false, message: validation.errorMessage }
              }}
            />
          </TabsContent>

          <TabsContent value="csv" data-testid="csv-upload-content">
            <CsvUploadTab
              existingUsers={existingUsers}
              currentEmails={emails}
              onEmailsAdd={addEmails}
            />
          </TabsContent>
        </Tabs>

        <EmailList
          emails={emails}
          onEmailRemove={removeEmail}
          onAllEmailsRemove={removeAllEmails}
        />

        <p className="text-sm text-foreground/50">
          Invited users will have access to shared compute resources and{' '}
          {storageLimitGB}GB of Storage.
        </p>

        <DialogFooter>
          <Button
            className="w-full"
            onClick={onInviteSubmit}
            disabled={!emails.length || sendingInvites}
            data-testid="send-invites-button"
          >
            {sendingInvites
              ? 'Sending Invites...'
              : `Send Invites (${emails.length})`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
